import { Autocomplete, Grid, OutlinedInput, TextField } from '@mui/material'
import { SCOPES } from 'components'
import { DeckMappDialog } from 'components/DeckMappDialog'
import { useCurrentUser } from 'hooks'
import { useSnackbar } from 'notistack'
import { addRole, updateRole } from 'pages/admin/common/data'
import { RolePermissions } from 'pages/users/components/RolePermissions'
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { groupBy } from 'common/arrays'

const RoleForm_ = forwardRef(function Form({ customers, sites, data, availableCognitoGroups }, ref) {
  const { currentUser } = useCurrentUser()

  const writeableCustomers = currentUser.getCustomersForScope(SCOPES.userManagement.Write)
  let selectedCustomer = data?.customer
  if (!selectedCustomer && data?.CustomerId) {
    const customer = customers.find((customer) => customer.id === data.CustomerId)
    selectedCustomer = {
      id: customer.id,
      label: customer.Name
    }
  }

  let selectedSites = data?.sites
  if (!selectedSites && data?.SiteIds) {
    selectedSites = sites
      .filter((site) => data.SiteIds.includes(site.id))
      .map((site) => {
        return {
          id: site.id,
          label: site.Name
        }
      })
  }

  let scopes = data?.scopes

  if (!scopes && data?.Scopes) {
    scopes = data.Scopes.flatMap((scope) => {
      const options = []
      if (scope.CanWrite) {
        options.push({
          name: scope.Name,
          label: `${scope.Name} - Write`,
          id: `${scope.Name}.Write`
        })
      }
      options.push({
        name: scope.Name,
        label: `${scope.Name} - Read`,
        id: `${scope.Name}.Read`
      })

      return options
    })
  }

  const [role, setRole] = useState(
    data
      ? {
          name: data.name ?? data.Name,
          description: data.description ?? data.Description,
          customer: {
            id: selectedCustomer.id,
            label: selectedCustomer.label
          },
          sites: selectedSites,
          cognitoGroup: data.cognitoGroup ?? data.Group,
          scopes
        }
      : {
          name: '',
          description: '',
          customer: null,
          sites: [],
          cognitoGroup: null,
          scopes: []
        }
  )

  useImperativeHandle(ref, () => {
    return {
      getValues() {
        let error = false
        if (!role.name.trim().length) {
          setErrors((errors) => {
            return {
              ...errors,
              name: 'Role name is required'
            }
          })
          error = true
        }

        if (!role.customer?.id) {
          setErrors((errors) => {
            return {
              ...errors,
              customer: 'Select the customer this role is for'
            }
          })
          error = true
        }

        if (!role.sites?.length) {
          setErrors((errors) => {
            return {
              ...errors,
              sites: 'Select the sites this role belongs to'
            }
          })
          error = true
        }

        if (!role.cognitoGroup?.length) {
          setErrors((errors) => {
            return {
              ...errors,
              cognitoGroup: 'Select a Cognito group'
            }
          })
          error = true
        }

        if (!role.scopes.length) {
          setErrors((errors) => {
            return {
              ...errors,
              scopes: 'Select role scopes'
            }
          })
          error = true
        }

        return {
          error,
          role
        }
      }
    }
  })

  const [errors, setErrors] = useState({})

  const filteredCustomers = useMemo(() => {
    return customers
      .filter((c) => currentUser.isAdmin || writeableCustomers.includes(c.id))
      .map((customer) => {
        return {
          id: customer.id,
          label: customer.Name
        }
      })
  }, [customers, writeableCustomers, currentUser.isAdmin])

  const filteredSites = useMemo(() => {
    return sites
      .filter((site) => site.CustomerId === role.customer?.id)
      .map((site) => {
        return {
          id: site.id,
          label: site.Name
        }
      })
  }, [role.customer, sites])

  const cognitoGroups = useMemo(() => {
    return availableCognitoGroups.map((group) => {
      return group.GroupName
    })
  }, [availableCognitoGroups])

  return (
    <Grid container flexDirection="column" spacing={1} alignItems="center">
      <Grid width="100%" item>
        <OutlinedInput
          error={!!errors.name}
          value={role.name}
          placeholder="Role name"
          color="secondary"
          fullWidth
          onChange={({ target: { value } }) => {
            setErrors((errors) => {
              return {
                ...errors,
                name: undefined
              }
            })
            setRole((role) => {
              return {
                ...role,
                name: value
              }
            })
          }}
        />
      </Grid>
      <Grid width="100%" item>
        <OutlinedInput
          value={role.description}
          error={!!errors.description}
          placeholder="Description"
          color="secondary"
          fullWidth
          onChange={({ target: { value } }) => {
            setErrors((errors) => {
              return {
                ...errors,
                description: ''
              }
            })
            setRole((user) => {
              return {
                ...user,
                description: value
              }
            })
          }}
        />
      </Grid>

      <Grid width="100%" item>
        <Autocomplete
          options={filteredCustomers}
          value={role.customer}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          getOptionLabel={(data) => data.label}
          onChange={(_, value) => {
            setErrors((errors) => {
              return {
                ...errors,
                customer: undefined
              }
            })
            setRole((role) => {
              return {
                ...role,
                customer: value,

                sites: []
              }
            })
          }}
          renderInput={(params) => (
            <TextField error={!!errors.customer} {...params} label="Select customer" variant="standard" />
          )}
        />
      </Grid>

      <Grid width="100%" item>
        <Autocomplete
          options={cognitoGroups}
          value={role.cognitoGroup}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          onChange={(_, value) => {
            setErrors((errors) => {
              return {
                ...errors,
                cognitoGroup: undefined
              }
            })
            setRole((role) => {
              return {
                ...role,
                cognitoGroup: value
              }
            })
          }}
          renderInput={(params) => (
            <TextField error={!!errors.cognitoGroup} {...params} label="Select Cognito group" variant="standard" />
          )}
        />
      </Grid>

      <Grid width="100%" item>
        <Autocomplete
          options={filteredSites}
          value={role.sites}
          multiple
          disableCloseOnSelect
          getOptionLabel={(data) => data.label}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          onChange={(_, value) => {
            setErrors((errors) => {
              return {
                ...errors,
                sites: undefined
              }
            })
            setRole((role) => {
              return {
                ...role,
                sites: value
              }
            })
          }}
          renderInput={(params) => (
            <TextField error={!!errors.sites} {...params} label="Select sites" variant="standard" />
          )}
        />
      </Grid>

      <RolePermissions role={role} errors={errors} setErrors={setErrors} setRole={setRole} />
    </Grid>
  )
})

export const RoleForm = ({ closeModal, customers, sites, data, availableCognitoGroups }) => {
  const ref = useRef()
  const [isLoading, setIsLoading] = useState(false)
  const [role_, setRole_] = useState(data)
  const { enqueueSnackbar } = useSnackbar()
  const { getTransactionMetaData } = useCurrentUser()

  const editRole = async (data) => {
    if (JSON.stringify(data) === JSON.stringify(role_)) {
      enqueueSnackbar('Role info has not changed')
      return
    }

    await updateRole(
      {
        id: role_.id,
        Name: data.name,
        Description: data.description,
        SiteIds: data.sites.map((site) => {
          return site.id
        }),
        CustomerId: data.customer.id,
        Group: data.cognitoGroup,
        Enabled: data.enabled,
        Scopes: transformScope(data.scopes)
      },
      getTransactionMetaData()
    )
    enqueueSnackbar(`Role Item updated successfully`, {
      variant: 'success'
    })

    closeModal()
  }

  const transformScope = (scopes) => {
    const grouped = groupBy('name')(scopes)
    return Object.keys(grouped).map((key) => {
      const canWrite = grouped[key].some((item) => item.id.includes('.Write'))
      return {
        Name: key,
        CanWrite: canWrite,
        CanRead: canWrite || grouped[key].some((item) => item.id.includes('.Read'))
      }
    })
  }

  const createNewRole = async (data) => {
    enqueueSnackbar(`Creating role ...`, {
      autoHideDuration: 2000
    })
    await addRole(
      {
        Name: data.name,
        Description: data.description,
        SiteIds: data.sites.map((site) => {
          return site.id
        }),
        CustomerId: data.customer.id,
        Group: data.cognitoGroup,
        Enabled: true,
        Scopes: transformScope(data.scopes)
      },
      getTransactionMetaData()
    )

    enqueueSnackbar(`Role successfully created ...`, {
      autoHideDuration: 2000,
      variant: 'success'
    })

    closeModal()
  }

  const onSaveClicked = async () => {
    const { error, role } = ref.current.getValues()

    if (error) {
      return
    }
    setIsLoading(true)
    try {
      if (role_?.id) {
        await editRole(role)
      } else {
        setRole_(role)
        await createNewRole(role)
      }
    } catch (e) {
      enqueueSnackbar('An error occured while saving role', {
        variant: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <DeckMappDialog
      onCancel={closeModal}
      saveButtonProps={{
        title: !data?.id ? 'Add role' : 'Edit role',
        onClick: onSaveClicked
      }}
      isLoading={isLoading}
      open
      title={!data?.id ? 'Add role' : 'Edit role'}>
      <RoleForm_
        ref={ref}
        data={role_}
        availableCognitoGroups={availableCognitoGroups}
        customers={customers}
        sites={sites}
      />
    </DeckMappDialog>
  )
}
